/**
 * ALERT_COPY — the standardized catalog of toast/banner copy.
 *
 * This is additive and framework-agnostic: it does not import
 * `react-hot-toast` or any Toast/Alert component, so it can be adopted one
 * call site at a time. A call site migrates from an inline string:
 *
 *   toast.success('Updated Successfully.')
 *
 * to a config lookup:
 *
 *   const { title, duration } = getAlertCopy('settings.profileUpdated')
 *   toast.success(title, { duration })
 *
 * Rules for entries in this file:
 *  1. Lead with the outcome, not the mechanism ("Link copied", not "Copy successful").
 *  2. One verb per action, everywhere — don't add a synonym for an action that already has an id.
 *  3. Errors always name a next step, unless the system is already retrying on its own.
 *  4. Never surface raw system errors (`e.toString()`, `JSON.stringify(...)`, status codes) —
 *     add a translated entry here instead, and fall back to `generic.unknownError`.
 *  5. Sentence case, minimal punctuation, no ALL CAPS.
 *  6. Name the object when it disambiguates ("Album deleted", not "Deleted Successfully.").
 *
 * IDs are dot-namespaced by feature so the catalog stays greppable and so two
 * features never fight over the same key. Group new entries under an
 * existing namespace before adding a new one.
 */

import { AlertCopyEntry, DEFAULT_ALERT_DURATIONS } from './types'

export const ALERT_COPY = {
  // ---------------------------------------------------------------------
  // generic / system-wide
  // ---------------------------------------------------------------------
  'generic.unknownError': {
    variant: 'error',
    title: 'Something went wrong — try again in a moment.',
  },
  'generic.offline': {
    variant: 'error',
    title: "You're offline",
    description: 'Reconnect and try again — nothing was lost.',
  },
  'generic.comingSoon': {
    variant: 'info',
    title: 'Coming soon',
    duration: 2600,
  },
  'generic.signInRequired': {
    variant: 'error',
    title: 'Sign in to continue',
  },

  // ---------------------------------------------------------------------
  // auth
  // ---------------------------------------------------------------------
  'auth.accountInactive': {
    variant: 'error',
    title: 'This account is inactive',
    description: 'Reactivate it to sign in, or create a new one.',
  },
  'auth.accountDeleted': {
    variant: 'error',
    title: "This account no longer exists",
    description: 'Create a new one to continue.',
  },
  'auth.sessionExpired': {
    variant: 'error',
    title: 'Your session expired — sign in again.',
  },
  'auth.passwordChangeFailed': {
    variant: 'error',
    title: "Couldn't change your password",
    description: 'Double-check your current password and try again.',
  },

  // ---------------------------------------------------------------------
  // album
  // ---------------------------------------------------------------------
  'album.created': {
    variant: 'success',
    title: 'Album created',
    duration: 3000,
  },
  'album.createFailed': {
    variant: 'error',
    title: "Couldn't create the album — try again.",
  },
  'album.deleted': {
    variant: 'success',
    title: 'Album deleted',
    duration: 3200,
  },
  'album.deleteFailed': {
    variant: 'error',
    title: "Couldn't delete the album — try again.",
  },
  'album.deleteForbidden': {
    variant: 'error',
    title: 'Only the owner can delete this album',
  },
  'album.updated': {
    variant: 'success',
    title: 'Album updated',
    duration: 2000,
  },
  'album.updateFailed': {
    variant: 'error',
    title: "Couldn't update the album — try again.",
  },

  // ---------------------------------------------------------------------
  // photo (within an album)
  // ---------------------------------------------------------------------
  'album.saveFirst': {
    variant: 'error',
    title: 'Save the album first',
  },
  'photo.added': {
    variant: 'success',
    title: 'Photo added',
    duration: 2400,
  },
  'photo.addedPlural': {
    variant: 'success',
    // Interpolate the count at the call site: `${n} photos added`
    title: 'photos added',
    duration: 2400,
  },
  'photo.uploadFailed': {
    variant: 'error',
    title: "Couldn't upload — try again.",
  },
  'photo.processFailed': {
    variant: 'error',
    title: "Couldn't process one of the photos",
  },
  'photo.noneUploaded': {
    variant: 'error',
    title: 'No photos uploaded',
  },
  'photo.noPhotosSelected': {
    variant: 'error',
    title: 'Add at least one photo first',
  },
  'photo.duplicateSelected': {
    variant: 'error',
    title: "You've already selected one of those files",
  },
  'photo.tooManyFiles': {
    variant: 'error',
    title: 'You can upload up to 5 files at a time',
  },
  'photo.fileTooLarge': {
    variant: 'error',
    title: 'Each photo can be up to 5MB',
  },
  'photo.deleted': {
    variant: 'success',
    title: 'Photo deleted',
    duration: 3200,
  },
  'photo.deletedPlural': {
    variant: 'success',
    // Interpolate the count at the call site: `${n} photos removed`
    title: 'photos removed',
    duration: 3200,
  },
  'photo.deleteFailed': {
    variant: 'error',
    title: "Couldn't delete that photo — try again.",
  },
  'photo.deleteUnavailable': {
    variant: 'error',
    title: "Can't delete this photo",
  },
  'photo.deleteForbidden': {
    variant: 'error',
    title: 'Only the owner can delete photos',
  },
  'photo.likeFailed': {
    variant: 'error',
    title: "Couldn't save your reaction — try again.",
  },
  'photo.commentSignInRequired': {
    variant: 'error',
    title: 'Sign in to comment',
  },
  'photo.commentFailed': {
    variant: 'error',
    title: "Couldn't post your comment — try again.",
  },
  'photo.commentsLoadFailed': {
    variant: 'error',
    title: "Couldn't load comments",
  },

  // ---------------------------------------------------------------------
  // sharing / keys
  // ---------------------------------------------------------------------
  'share.linkCopied': {
    variant: 'success',
    title: 'Link copied',
    duration: 2800,
  },
  'share.linkCopyFailed': {
    variant: 'error',
    title: "Couldn't copy the link — try again.",
  },
  'share.qrDownloadFailed': {
    variant: 'error',
    title: "Couldn't download the QR code — try again.",
  },
  'share.guestKeyCopied': {
    variant: 'success',
    title: 'Guest key copied — same key as the album',
    duration: 3000,
  },
  'share.madePublic': {
    variant: 'success',
    title: 'Now public — anyone with the link can view',
    duration: 3200,
  },
  'share.madePrivate': {
    variant: 'info',
    title: 'Private again — the old link stopped working',
    duration: 3200,
  },
  'share.privacyUpdateFailed': {
    variant: 'error',
    title: "Couldn't update visibility — try again.",
  },
  'share.downloadOn': {
    variant: 'success',
    title: 'Download originals on',
    duration: 2800,
  },
  'share.downloadOff': {
    variant: 'info',
    title: 'Download originals off — viewing only',
    duration: 3200,
  },
  'share.downloadUpdateFailed': {
    variant: 'error',
    title: "Couldn't update download setting — try again.",
  },
  'share.uploadQualityOn': {
    variant: 'success',
    title: 'Upload quality set to Full — up to 4000px',
    duration: 2800,
  },
  'share.uploadQualityOff': {
    variant: 'info',
    title: 'Upload quality set to Standard — 1000px',
    duration: 2800,
  },
  'share.uploadQualityUpdateFailed': {
    variant: 'error',
    title: "Couldn't update upload quality — try again.",
  },
  'share.coverUpdated': {
    variant: 'success',
    title: 'Album cover updated',
    duration: 2800,
  },
  'share.coverUpdateFailed': {
    variant: 'error',
    title: "Couldn't update the cover photo — try again.",
  },
  'share.reorderUpdateFailed': {
    variant: 'error',
    title: "Couldn't save the new order — try again.",
  },
  'share.turnTo3dFailed': {
    variant: 'error',
    title: "Couldn't create the 3D gallery — try again.",
  },
  'share.turnTo3dCreated': {
    variant: 'success',
    title: 'Gallery created — photos hung on the walls',
    duration: 2800,
  },

  // ---------------------------------------------------------------------
  // gallery studio (2D)
  // ---------------------------------------------------------------------
  'galleryStudio.renamed': {
    variant: 'success',
    title: 'Renamed',
    duration: 2000,
  },
  'galleryStudio.updated': {
    variant: 'success',
    title: 'Updated',
    duration: 2000,
  },
  'galleryStudio.updateFailed': {
    variant: 'error',
    title: "Couldn't update — try again.",
  },
  'galleryStudio.deleted': {
    variant: 'success',
    title: 'Deleted',
    duration: 2000,
  },
  'galleryStudio.deleteFailed': {
    variant: 'error',
    title: "Couldn't delete — try again.",
  },
  'galleryStudio.duplicated': {
    variant: 'success',
    title: 'Duplicated — opening your copy',
    duration: 2000,
  },
  'galleryStudio.duplicateFailed': {
    variant: 'error',
    title: "Couldn't duplicate — try again.",
  },
  'galleryStudio.saveFailedRetrying': {
    variant: 'error',
    title: 'Failed to save — retrying on your next edit',
  },
  'galleryStudio.screenshotSaved': {
    variant: 'success',
    title: 'Screenshot saved to album',
    duration: 2500,
  },
  'galleryStudio.screenshotSaveFailed': {
    variant: 'error',
    title: "Couldn't save the screenshot — try again.",
  },
  'galleryStudio.signInToCreate': {
    variant: 'error',
    title: 'Sign in to create a gallery',
  },
  'galleryStudio.needsAtLeastOnePhoto': {
    variant: 'error',
    title: 'Add at least one photo before creating a gallery',
  },

  // ---------------------------------------------------------------------
  // gallery studio 3D — already the model for the rest of the app; kept
  // as-is here so it becomes the single source instead of a second copy.
  // ---------------------------------------------------------------------
  'studio3d.roomLoadFailed': {
    variant: 'error',
    // Interpolate the reason at the call site: `Couldn't load that room — ${reason}`
    title: "Couldn't load that room",
  },
  'studio3d.hung': {
    variant: 'success',
    // Interpolate the print name at the call site.
    title: 'Hung — swap the photo from Assets',
    duration: 3000,
  },
  'studio3d.photoSwapped': {
    variant: 'success',
    title: 'Photo swapped — frame and light kept',
  },
  'studio3d.takenDown': {
    variant: 'info',
    // Interpolate the print name at the call site: `"${name}" taken down`
    title: 'taken down',
  },
  'studio3d.selectPrintFirst': {
    variant: 'info',
    title: 'Select a print first — or press H to hang it fresh',
  },
  'studio3d.notePinned': {
    variant: 'success',
    title: 'Note pinned — visible to everyone with the key',
    duration: 3000,
  },
  'studio3d.museRehung': {
    variant: 'info',
    title: 'MUSE — rehung by pace & color, eye line normalized (heuristic, not AI)',
    duration: 4200,
  },
  'studio3d.guestKeyCopied': {
    variant: 'success',
    title: 'Guest key copied — same key as the album',
  },
  'studio3d.copyFailed': {
    variant: 'error',
    title: 'Could not copy — copy the link from your browser bar instead',
  },
  'studio3d.multiRoomGated': {
    variant: 'info',
    title: 'Multi-room galleries ship with the Studio plan',
  },
  'studio3d.saveFailedRetrying': {
    variant: 'error',
    title: 'Failed to save — retrying on your next edit',
  },
  'studio3d.renameFailed': {
    variant: 'error',
    title: 'Failed to rename — try again',
  },

  // ---------------------------------------------------------------------
  // settings — profile
  // ---------------------------------------------------------------------
  'settings.profileUpdated': {
    variant: 'success',
    title: 'Profile updated',
    duration: 2400,
  },
  'settings.profileUpdateFailed': {
    variant: 'error',
    title: "Couldn't update your profile — try again.",
  },
  'settings.privacySaved': {
    variant: 'success',
    title: 'Privacy settings saved',
    duration: 2400,
  },
  'settings.privacySaveFailed': {
    variant: 'error',
    title: "Couldn't save privacy settings — try again.",
  },

  // ---------------------------------------------------------------------
  // settings — billing
  // ---------------------------------------------------------------------
  'settings.subscribed': {
    variant: 'success',
    title: "You're subscribed — your plan is active.",
    duration: 3600,
  },
  'settings.checkoutCancelled': {
    variant: 'info',
    title: 'Checkout was cancelled — no charge was made.',
  },
  'settings.checkoutFailed': {
    variant: 'error',
    title: "Couldn't start checkout — try again.",
  },

  // ---------------------------------------------------------------------
  // settings — account & danger zone
  // ---------------------------------------------------------------------
  'settings.dangerZoneCaution': {
    variant: 'warning',
    title: 'Proceed with caution',
    description:
      'Deactivating pauses your account; deleting removes it — and everything in it — for good.',
  },
  'settings.accountDeactivated': {
    variant: 'success',
    title: 'Account deactivated',
    description: 'Reactivate anytime by logging back in.',
    duration: 4000,
  },
  'settings.accountDeactivateFailed': {
    variant: 'error',
    title: "Couldn't deactivate your account — try again.",
  },
  'settings.accountDeleted': {
    variant: 'success',
    title: 'Account deleted',
    duration: 3200,
  },
  'settings.accountDeleteFailed': {
    variant: 'error',
    title: "Couldn't delete your account — try again.",
  },

  // ---------------------------------------------------------------------
  // connections
  // ---------------------------------------------------------------------
  'connection.requestSent': {
    variant: 'success',
    title: 'Request sent',
    duration: 2400,
  },
  'connection.requestFailed': {
    variant: 'error',
    title: "Couldn't send that request — try again.",
  },
  'connection.accepted': {
    variant: 'success',
    title: 'Connected',
    duration: 2400,
  },
  'connection.acceptFailed': {
    variant: 'error',
    title: "Couldn't accept that request — try again.",
  },
  'connection.declined': {
    variant: 'info',
    title: 'Request declined',
    duration: 2400,
  },
  'connection.declineFailed': {
    variant: 'error',
    title: "Couldn't decline that request — try again.",
  },
  'connection.cancelled': {
    variant: 'info',
    title: 'Request cancelled',
    duration: 2400,
  },
  'connection.cancelFailed': {
    variant: 'error',
    title: "Couldn't cancel that request — try again.",
  },
  'connection.removed': {
    variant: 'success',
    title: 'Connection removed',
    duration: 2800,
  },
  'connection.removeFailed': {
    variant: 'error',
    title: "Couldn't remove that connection — try again.",
  },
  'connection.blocked': {
    variant: 'success',
    title: 'Blocked',
    description: 'They can no longer see your profile or reach you.',
    duration: 3200,
  },
  'connection.blockFailed': {
    variant: 'error',
    title: "Couldn't block that person — try again.",
  },
} satisfies Record<string, AlertCopyEntry>

export type AlertCopyId = keyof typeof ALERT_COPY

/**
 * Look up a standardized alert by id, with its duration resolved (entry
 * duration wins, falling back to the variant default, falling back to
 * "no auto-dismiss").
 */
export function getAlertCopy(id: AlertCopyId): AlertCopyEntry & { duration?: number } {
  const entry: AlertCopyEntry = ALERT_COPY[id]
  const duration = entry.duration ?? DEFAULT_ALERT_DURATIONS[entry.variant]
  return { ...entry, duration }
}
